'use client';

import { useCallback, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { AlertTriangle, Check, ImagePlus, StickyNote } from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import { useConfirmBuildingAngle } from '@/shared/api/hooks/use-project-angles';
import { ApiClientError } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';
import { DIFFERENT_BUILDING_REASON } from '@/types/project-angle';
import {
  AngleImageWithProgress,
  type RenderOverlayPhase,
} from '@/features/design/render';
import { useRenderStore } from '@/features/design/render/model/render-form.store';
import { ConceptActions, RenderInfoSheet } from '@/features/design/concept';
import { AngleImageFrame } from './AngleImageFrame';
import { LlmFeedbackImageOverlay } from './LlmFeedbackImageOverlay';
import { ANGLE_OVERLAY_BUTTON } from './angle-overlay-chrome';
import {
  RenderFeedbackChips,
  RenderFeedbackBanners,
} from '@/features/design/render/ui/RenderFeedbackGroup';
import { StudioBridgeNotice } from '@/features/design/render/ui/StudioBridgeNotice';
import { useOptionalDesktopLayout } from '@/features/project';
import { EditRegionsLayer } from '@/features/design/render/ui/edit-regions/EditRegionsLayer';
import {
  ConstructedViewPlaceholder,
  ReconstructionMarker,
} from '@/features/project/ui/ReconstructionMarker';
import { useBreakpoint } from '@/shared/hooks/use-media-query';
import type { ImageSizingConstraints } from '@/shared/hooks';
import type { ConceptAngle, Angle } from '@/types/concept';
import type { Concept } from '@/types/concept';
import type { Project } from '@/types/project';
import type { Render } from '@/types/render';
import type { Job } from '@/features/design/job-queue';
import { NoticeBanner } from '@/shared/ui';

interface MainAngleDisplayProps {
  project: Project;
  concept: Concept;
  mainAngle: ConceptAngle;
  angles: Record<string, Angle>;
  currentRender: Render | undefined;
  renderFileUrl: string | undefined;
  shouldShowRender: boolean;
  isRendering: boolean;
  activeRenderJob: Job | undefined;
  activeUpscaleJob: Job | undefined;
  /** Just-completed render still prefetching its image — holds the progress
   *  overlay until it is decoded (Apple/Android parity). */
  isPreloadingRender?: boolean;
  /** Pulse trigger for the positive LLM-feedback icon — see LlmFeedbackImageOverlay. */
  feedbackPulseSignal?: string | null;
  hideRenderButton: boolean;
  onToggleShowRender: () => void;
  onDraggingSliderChange?: (isDragging: boolean) => void;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onStartNewPhoto: () => void;
  onReplacePhoto: () => void;
  onEditNotes?: () => void;
  onRenameAngle?: () => void;
  onEditAngles?: () => void;
  onDeleteConcept?: () => void;
  onDeleteRender?: (renderId: string) => void;
  onSaveAsReference?: () => void;
  isSavingAsReference?: boolean;
  /** Per-layout sizing constraints (Apple-parity height cap + floor). */
  sizing: ImageSizingConstraints;
  isArchived?: boolean;
}

export function MainAngleDisplay({
  project,
  concept,
  mainAngle,
  angles,
  currentRender,
  renderFileUrl,
  shouldShowRender,
  isRendering,
  activeRenderJob,
  activeUpscaleJob,
  isPreloadingRender = false,
  onToggleShowRender,
  onDraggingSliderChange,
  onSwipeLeft,
  onSwipeRight,
  onStartNewPhoto,
  onReplacePhoto,
  onEditNotes,
  onRenameAngle,
  onEditAngles,
  onDeleteConcept,
  onDeleteRender,
  onSaveAsReference,
  isSavingAsReference = false,
  feedbackPulseSignal,
  sizing,
  isArchived = false,
}: MainAngleDisplayProps) {
  const tAngle = useTranslations('angle.menu');
  const tOptions = useTranslations('render.options');
  const router = useRouter();
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [isInfoSheetOpen, setIsInfoSheetOpen] = useState(false);
  // Ratio the image box is actually shaped to, reported by AngleImageDisplay
  // once the browser decodes the image (Apple `mainImageAspectRatio`).
  const [measuredAspectRatio, setMeasuredAspectRatio] = useState<number | null>(
    null
  );

  // Two-column (xl+ inspector) layout keeps LLM feedback as expanded banners
  // below the image (collapsible to in-flow chips). Single-column collapses it
  // to icons pinned on the image — there's no room for open banners in the
  // stacked image+settings scroll. Mirrors `AngleImageFrame`'s own signal.
  const desktopLayout = useOptionalDesktopLayout();
  const { isXl } = useBreakpoint();
  const isTwoColumnLayout = !!(isXl && desktopLayout);
  // The progress overlay outlives the queue entry (it holds through the image
  // prefetch, then dissolves over 1.2 s). Card chrome must wait for that whole
  // window: gating on the job alone popped the feedback icons and banners back
  // while the card was still dark. AngleImageWithProgress owns the lifecycle
  // and reports it here.
  const [isOverlayUp, setIsOverlayUp] = useState(false);
  const handleOverlayPhaseChange = useCallback(
    (phase: RenderOverlayPhase, hasJob: boolean) => {
      setIsOverlayUp(phase !== 'hidden' && hasJob);
    },
    []
  );
  const showFeedbackForRender =
    currentRender && !isRendering && !activeRenderJob && !isOverlayUp;

  // The single-angle viewer is mode-driven in BOTH arrangements (two-column
  // inspector and single-column stacked) — there is one angle on screen and it
  // switches as the user toggles Explore/Refine, so the toggle no longer needs
  // its own duplicate preview (see UnifiedRenderPanel `hideTogglePreview`).
  // Slider clip semantics (LTR):
  //   position = 0   → afterImage (render) fully visible
  //   position = 100 → beforeImage (source) fully visible
  // So Refine → 0 (show render), Recreate/Explore → 100 (show source photo).
  // Animation matches RenderModeToggle (450ms cubic-bezier). User can still
  // drag manually — drag flips the slider into an override state that resets
  // only when the mode prop changes again.
  const renderMode = useRenderStore((state) => state.formData.mode);
  const sliderProgrammaticPosition = renderMode === 'refine' ? 0 : 100;

  const angleData = mainAngle.relationships?.angle?.data?.id
    ? angles[mainAngle.relationships.angle.data.id]
    : undefined;
  const angleMeta = mainAngle.relationships?.angle?.meta;
  const angleFileUrl = angleData?.attributes?.file?.url || angleMeta?.file_url;
  // A CONSTRUCTED view has no "before": no source photo, no comparison slider,
  // no fullscreen original. Everything below is already keyed off the (null)
  // file URL, so this flag only has to keep the view from being mistaken for a
  // broken upload — see the empty-frame guard right after.
  const hasSourceImage = angleMeta?.has_source_image !== false;
  // Results of a building that does not exist are derived, not photographed —
  // the marker rides on every frame (product rule 5).
  const isReconstruction = project.attributes?.is_reconstruction === true;

  // Seed the container shape from the API-known angle dimensions (concept-angle
  // meta, with the full Angle resource's file dims as a fallback) so it doesn't
  // reflow from 16:9 once the browser decodes the image.
  const angleWidth = angleMeta?.width ?? angleData?.attributes?.file?.width;
  const angleHeight = angleMeta?.height ?? angleData?.attributes?.file?.height;
  const initialAspectRatio =
    angleWidth && angleHeight && angleHeight > 0
      ? angleWidth / angleHeight
      : undefined;

  // Shape the card frame matches its strips to (Apple `mainImageAspectRatio`).
  // The loaded image reports it; until then the API-known ratio keeps the frame
  // right on the first paint instead of settling after decode.
  const frameAspectRatio = measuredAspectRatio ?? initialAspectRatio ?? null;

  const angleValidation = mainAngle.relationships?.angle?.meta?.validation;
  const needsReplacement =
    angleValidation?.is_deleted || angleValidation?.can_replace;
  // Strict different-building model (cross-app parity): a different-building main
  // angle offers ONLY "It's my building" (confirm-building → valid view) beside
  // the usual replace affordance — no "Create new building". Apple/Android
  // dropped that affordance from their concept-editor strict surfaces too.
  const isDifferentBuilding =
    angleValidation?.validation_failure_reason === DIFFERENT_BUILDING_REASON;
  // "It's my building" override — shown when the server says the false rejection
  // can be lifted. POST /angles/{id}/confirm-building flips the angle back to
  // Valid; the invalidated concept/project queries clear this banner.
  const canOverrideBuilding =
    isDifferentBuilding && angleValidation?.can_override_building === true;
  const angleId = mainAngle.relationships?.angle?.data?.id;
  const confirmBuilding = useConfirmBuildingAngle(project.id);
  const handleConfirmBuilding = () => {
    if (!angleId) return;
    confirmBuilding.mutate(angleId, {
      onError: (error) => {
        toast.error(
          error instanceof ApiClientError
            ? error.getFirstError()
            : tOptions('confirmBuildingError')
        );
      },
    });
  };

  // Invalid angle with no image — show replacement placeholder.
  // A constructed view is EXCLUDED from this branch: it legitimately has no
  // file and no render until the first generation, and bailing out here left
  // the whole design page blank for a building with no photo.
  if (hasSourceImage && !angleFileUrl && !renderFileUrl && !activeRenderJob) {
    if (needsReplacement) {
      return (
        <div className="relative mb-4">
          <div className="relative border border-border rounded-lg overflow-hidden bg-image-backdrop">
            <div className="aspect-[16/9] flex flex-col items-center justify-center gap-3 p-6">
              <AlertTriangle className="h-10 w-10 text-destructive" />
              {angleValidation?.recommendations && (
                <p className="text-form-help text-muted-foreground text-center max-w-md">
                  {angleValidation.recommendations}
                </p>
              )}
              <div className="flex flex-wrap items-center justify-center gap-2">
                {canOverrideBuilding && (
                  <Button
                    variant="default"
                    onClick={handleConfirmBuilding}
                    disabled={confirmBuilding.isPending}
                  >
                    <Check className="h-4 w-4 me-2" />
                    {tOptions('itsMyBuilding')}
                  </Button>
                )}
                <Button
                  variant={canOverrideBuilding ? 'outline' : 'default'}
                  onClick={onReplacePhoto}
                  disabled={confirmBuilding.isPending}
                >
                  <ImagePlus className="h-4 w-4 me-2" />
                  {tOptions('replaceSourcePhoto')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  // Actions bar (warning icon + menu). Each button keeps a 44px hit area
  // with a visible 28px circle painted via `before:` pseudo-element — same
  // construction as the name/refine pills above. Pills sit flush with the
  // top-right corner; the extra hit area extends into the corner.
  const actionsBarContent = !concept.attributes.is_main_angle_generated && !isOverlayUp && (
    <div className="flex items-center">
      {/* Filtered-content feedback now lives as a tappable icon pinned to the
          image's bottom-center (LlmFeedbackImageOverlay) — no separate warning
          button / scroll-to-banner affordance. */}
      <ConceptActions
        projectId={project.id}
        conceptId={concept.id}
        conceptName={
          concept.attributes.title ||
          project.attributes?.name ||
          'Concept'
        }
        conceptSequenceNumber={concept.attributes.project_sequence_number}
        mode="editor"
        isDerivative={false}
        hasRender={!!currentRender}
        renderId={currentRender?.id}
        renderFileUrl={renderFileUrl}
        isUpscaled={currentRender?.attributes?.is_upscaled || false}
        studioBridge={currentRender?.attributes?.studio_bridge}
        onStartNewPhoto={onStartNewPhoto}
        onReplacePhoto={onReplacePhoto}
        onRenameAngle={onRenameAngle}
        onEditAngles={onEditAngles}
        onDeleteConcept={onDeleteConcept}
        onDeleteRender={onDeleteRender}
        onSaveAsReference={onSaveAsReference}
        isSavingAsReference={isSavingAsReference}
        onShowRenderInfo={
          currentRender ? () => setIsInfoSheetOpen(true) : undefined
        }
        className={ANGLE_OVERLAY_BUTTON}
      />
    </div>
  );

  return (
    <div>
      {/* Main angle image or render. Frame styling (settings-card surface in
          two-column, tight border in single-column) and auto vertical padding
          live in AngleImageFrame so derivative angles get identical treatment. */}
      <AngleImageFrame
        className="mb-4"
        sizing={sizing}
        // Only a real image box leaves strips; the placeholder branches below
        // fill the frame width, so they get no padding.
        imageAspectRatio={
          angleFileUrl || renderFileUrl ? frameAspectRatio : null
        }
      >
        {/* Phone (< sm): square corners — the card rides full-bleed (see
            AngleImageFrame). `sm`+ keeps the rounded panel. */}
        <div className="relative rounded-none sm:rounded-lg overflow-hidden">
          {/* Top-leading overlay row: collapsed LLM-feedback icons
              (single-column only). The custom view name is no longer painted on
              the image — it lives in the angle tab label now (see
              ConceptContentTabs). Rename stays in the ⋯ menu (ConceptActions). */}
          {!isTwoColumnLayout && showFeedbackForRender && (
            <div className="absolute top-0 start-0 z-10 flex items-center">
              <LlmFeedbackImageOverlay
                render={currentRender}
                pulseSignal={feedbackPulseSignal}
              />
            </div>
          )}
          {angleFileUrl || renderFileUrl ? (
            <AngleImageWithProgress
              sourceUrl={angleFileUrl}
              renderUrl={renderFileUrl}
              alt={
                mainAngle.relationships?.angle?.meta?.name ||
                tAngle('mainAngle')
              }
              showingRender={shouldShowRender}
              onToggle={onToggleShowRender}
              isUpscaled={currentRender?.attributes?.is_upscaled || false}
              activeRenderJob={activeRenderJob}
              activeUpscaleJob={activeUpscaleJob}
              isPreloadingRender={isPreloadingRender}
              conceptId={concept.id}
              projectId={project.id}
              onDraggingChange={onDraggingSliderChange}
              onSwipeLeft={onSwipeLeft}
              onSwipeRight={onSwipeRight}
              onImageClick={() => setIsViewerOpen(true)}
              isViewerOpenExternal={isViewerOpen}
              onViewerClose={() => setIsViewerOpen(false)}
              onOverlayPhaseChange={handleOverlayPhaseChange}
              sizing={sizing}
              sliderProgrammaticPosition={sliderProgrammaticPosition}
              initialAspectRatio={initialAspectRatio}
              onAspectRatioChange={setMeasuredAspectRatio}
            />
          ) : !hasSourceImage ? (
            /* Constructed view, nothing generated yet — say what the empty
               frame is waiting for instead of showing a grey box. */
            <ConstructedViewPlaceholder />
          ) : (
            /* Show placeholder if rendering but no image yet */
            <div className="aspect-[16/9] bg-image-backdrop" />
          )}

          {/* Permanent reconstruction marker — bottom-leading so it never
              collides with the LLM-feedback icons (top-leading) or the ⋯ menu
              (top-trailing). */}
          {isReconstruction && (
            <div className="absolute bottom-2 start-2 z-10">
              <ReconstructionMarker compact />
            </div>
          )}

          {/* Mark areas on the render for the next refine. */}
          {!isArchived && shouldShowRender && renderFileUrl && (
            <EditRegionsLayer
              conceptAngleId={mainAngle.id}
              currentRender={currentRender}
              isBusy={
                isRendering ||
                !!activeRenderJob ||
                !!activeUpscaleJob ||
                isOverlayUp
              }
            />
          )}

          {actionsBarContent && (
            <div className="absolute top-0 end-0">
              {actionsBarContent}
            </div>
          )}

        </div>
      </AngleImageFrame>

      {/* Concept note — render-adjacent banner in flow. The render button moved
          to the floating Generate capsule (single-column) / inspector footer
          (two-column), the favorite moved to the page header. */}
      {concept.attributes.note && !isArchived && (
        <button
          onClick={onEditNotes}
          className="mt-2 w-full text-start cursor-pointer hover:opacity-90 transition-opacity"
          type="button"
        >
          <NoticeBanner
            variant="info"
            icon={StickyNote}
            message={concept.attributes.note}
            titleWeight="font-normal"
          />
        </button>
      )}

      {/* LLM feedback (two-column) — expanded banners below the image (after the
          note, matching Apple/Android), collapsible to in-flow chips above
          them. Single-column shows icons pinned on the image instead. */}
      {isTwoColumnLayout && showFeedbackForRender && (
        <div className="mt-2 flex animate-fade-in flex-col gap-2">
          <RenderFeedbackChips render={currentRender} />
          <RenderFeedbackBanners render={currentRender} />
        </div>
      )}

      {/* Studio offer under the render (server picks the one render). */}
      {shouldShowRender && !isOverlayUp && (
        <StudioBridgeNotice render={currentRender} className="mt-2" />
      )}

      {/* Render-info sheet — opened via the "Information" item in the
          angle's overflow menu. */}
      {currentRender && (
        <RenderInfoSheet
          render={currentRender}
          projectId={project.id}
          currentAngleId={mainAngle.id}
          open={isInfoSheetOpen}
          onOpenChange={setIsInfoSheetOpen}
          onNavigateToParent={(parentConceptId) => {
            // Main-angle refine parents always live in another concept (each
            // main-render call creates a new concept), so swap-in-place is
            // not applicable here — always navigate.
            router.push(
              `/project/${project.id}/concept/${parentConceptId}`
            );
          }}
        />
      )}
    </div>
  );
}
